import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  disconnectPersonalConnection,
  listPersonalConnections,
  savePersonalApiKey,
  testPersonalConnection,
} from '@/services/personal-connections/personal-connections';
import { TestResult } from '@/services/personal-connections/types';

export const personalConnectionsQueryKey = (orgId?: string) => [
  'personal-connections',
  orgId,
];

/** The member's connections plus the non-popup actions (test, key, disconnect). */
export const usePersonalConnections = (orgId?: string) => {
  const queryClient = useQueryClient();
  const [tests, setTests] = useState<Record<string, TestResult>>({});

  const query = useQuery({
    queryKey: personalConnectionsQueryKey(orgId),
    queryFn: () => listPersonalConnections(orgId!),
    enabled: !!orgId,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: personalConnectionsQueryKey(orgId),
      }),
    [orgId, queryClient],
  );

  const test = useMutation({
    mutationFn: (provider: string) => testPersonalConnection(orgId!, provider),
    onSuccess: (result, provider) =>
      setTests((prev) => ({ ...prev, [provider]: result })),
    onSettled: () => void refresh(),
  });

  const saveKey = useMutation({
    mutationFn: ({ provider, apiKey }: { provider: string; apiKey: string }) =>
      savePersonalApiKey(orgId!, provider, apiKey),
    onSettled: () => void refresh(),
  });

  const disconnect = useMutation({
    mutationFn: ({
      provider,
      pendingOnly,
    }: {
      provider: string;
      pendingOnly: boolean;
    }) => disconnectPersonalConnection(orgId!, provider, pendingOnly),
    onSuccess: (_result, { provider }) =>
      setTests((prev) => {
        const next = { ...prev };
        delete next[provider];
        return next;
      }),
    onSettled: () => void refresh(),
  });

  return { query, tests, test, saveKey, disconnect };
};
